import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../../shared/format'
import type { Stage } from '../../types'

function Marker({ stage }: { stage: Stage }) {
  const done = stage.status === 'done'
  const active = stage.status === 'in_progress'
  return (
    <span
      className={cn(
        'relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border-2 bg-card',
        done && 'border-success bg-success text-white',
        active && 'border-primary ring-4 ring-primary/15',
        !done && !active && 'border-border',
      )}
    >
      {done ? <Check className="size-4" aria-hidden /> : active ? <span className="size-3 rounded-full bg-primary" aria-hidden /> : null}
    </span>
  )
}

/** Kurulum aşamalarının ilerleme çizelgesi: dar alanda dikey, genişte yatay. */
export function StageStepper({ stages }: { stages: Stage[] }) {
  const { t } = useTranslation()
  if (stages.length === 0) return null
  const doneBefore = (i: number) => i > 0 && stages[i - 1]?.status === 'done'

  return (
    <div className="@container">
      <ol className="@2xl:hidden" aria-label={t('projects.install.stages')}>
        {stages.map((s, i) => (
          <li key={s.id} className="relative flex gap-3 pb-4 last:pb-0" aria-current={s.status === 'in_progress' ? 'step' : undefined}>
            {i < stages.length - 1 ? <span className={cn('absolute bottom-0 left-4 top-8 w-0.5 -translate-x-1/2', s.status === 'done' ? 'bg-primary' : 'bg-border')} aria-hidden /> : null}
            <Marker stage={s} />
            <div className="min-w-0 pt-1">
              <p className="text-sm font-semibold leading-tight">{s.name}</p>
              <p className="text-xs text-muted-foreground">
                {fmtDate(s.plannedDate)} · {t(`projects.stageStatus.${s.status}`)}
              </p>
            </div>
          </li>
        ))}
      </ol>
      <ol className="hidden @2xl:flex" aria-label={t('projects.install.stages')}>
        {stages.map((s, i) => (
          <li key={s.id} className="relative flex min-w-0 flex-1 flex-col items-center text-center" aria-current={s.status === 'in_progress' ? 'step' : undefined}>
            {i > 0 ? <span className={cn('absolute right-1/2 top-4 h-0.5 w-full -translate-y-1/2', doneBefore(i) ? 'bg-primary' : 'bg-border')} aria-hidden /> : null}
            <Marker stage={s} />
            <span className="mt-2 px-1 text-xs font-semibold leading-tight">{s.name}</span>
            <span className="text-[11px] text-muted-foreground">{fmtDate(s.plannedDate)}</span>
            <span className="sr-only">{t(`projects.stageStatus.${s.status}`)}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}
